import {inject, Injectable, NgZone} from "@angular/core";
import {HttpClient, HttpContext} from "@angular/common/http";
import {HttpBaseService} from "./http-base.service";
import {MessageService} from "./message.service";
import {Subject, Subscription} from "rxjs";
import {retry, timeout} from "rxjs/operators";
import {MessageHandler} from "src/app/feature/chatbot/chat/messageHandler";
import {BYPASS_ERROR} from "../../interceptors/server-error.interceptor";

@Injectable({
	providedIn: "root",
})
export class ChatBotService extends HttpBaseService {
	public http = inject(HttpClient);
	public messageService = inject(MessageService);
	private readonly ngZone = inject(NgZone);

	private readonly apiUrl = "/mep/api/chatbot/";
	public backendMessageSubject: Subject<string> = new Subject<string>();
	private pollingSubscription?: Subscription;

	constructor() {
		super();
	}

	sendMessage(
		input: string,
		businessUnit: string,
		ignoreCapa: boolean,
		minimumAvailability: number,
		crossBusinessUnitSearch: boolean,
	) {
		this.http
			.post(
				this.apiUrl + "sendMessage",
				{input, businessUnit, ignoreCapa, minimumAvailability, crossBusinessUnitSearch},
				{responseType: "text"},
			)
			.subscribe((requestID) => {
				this.getMessage(requestID);
			});
	}

	getMessage(requestID: string) {
		// Cancel previous polling if exists
		if (this.pollingSubscription) {
			this.pollingSubscription.unsubscribe();
		}

		this.ngZone.runOutsideAngular(() => {
			this.pollingSubscription = this.http
				.post(this.apiUrl + "getMessage/", requestID, {
					responseType: "text",
					context: new HttpContext().set(BYPASS_ERROR, true),
				})
				.pipe(
					timeout(600000), // 10 minutes timeout to match backend
					retry(3), // Retry up to 3 times on failures
				)
				.subscribe({
					next: (message) => {
						this.ngZone.run(() => {
							this.backendMessageSubject.next(message);
							if (JSON.parse(message).output) {
								this.endRequest(requestID);
							} else {
								this.getMessage(requestID);
							}
						});
					},
					error: () => {
						// Silently handle errors - don't show error message to user
						// Keep existing chat data and UI state intact
						this.ngZone.run(() => {
							this.endRequest(requestID);
						});
					},
				});
		});
	}

	endRequest(requestID: string) {
		// Cancel polling when ending request
		if (this.pollingSubscription) {
			this.pollingSubscription.unsubscribe();
		}

		this.http.delete(this.apiUrl + "endRequest/" + requestID).subscribe();
	}

	public stopPolling() {
		if (this.pollingSubscription) {
			this.pollingSubscription.unsubscribe();
		}
	}

	public getMessageHandler(): MessageHandler {
		return new MessageHandler(this);
	}
}
